import { useLocation } from "react-router-dom";
import invariant from "ts-invariant";

import { useProfileContext } from "contexts/ProfileContext";
import {
  type UserProfile,
  userIsBillingAdmin,
  userIsPartner,
  userIsPhysician,
  userIsTherapist,
} from "utils/profile/profileHelper";

import { AboutYou, Complete, PartnerWelcomePage, ProfilePicture, WelcomePage } from "./views";

type OnboardingRoute = {
  path: string;
  element: JSX.Element;
};

const ROUTES: Record<string, OnboardingRoute> = {
  welcome: { path: "/", element: <WelcomePage /> },
  partnerWelcome: { path: "/", element: <PartnerWelcomePage /> },
  profilePicture: { path: "profile-picture", element: <ProfilePicture /> },
  aboutYou: { path: "about-you", element: <AboutYou /> },
  complete: { path: "complete", element: <Complete /> },
  completeRoot: { path: "/", element: <Complete /> },
} as const;

type OnboardingFlow = {
  routes: OnboardingRoute[];
  matcher: (profile: UserProfile) => boolean;
};

const ONBOARDING_FLOWS: OnboardingFlow[] = [
  {
    matcher: userIsTherapist,
    routes: [ROUTES.welcome, ROUTES.profilePicture, ROUTES.aboutYou, ROUTES.complete],
  },
  {
    matcher: userIsPhysician,
    routes: [ROUTES.completeRoot],
  },
  {
    matcher: userIsPartner,
    routes: [ROUTES.partnerWelcome, ROUTES.complete],
  },
  {
    matcher: userIsBillingAdmin,
    routes: [ROUTES.welcome, ROUTES.complete],
  },
  {
    matcher: () => true,
    routes: [ROUTES.welcome, ROUTES.profilePicture, ROUTES.aboutYou, ROUTES.complete],
  },
];

export const getOnboardingFlow = (profile: UserProfile): OnboardingRoute[] => {
  const flow = ONBOARDING_FLOWS.find(f => f.matcher(profile));
  return flow?.routes || [];
};

const getRouteUrlFromPath = (path: string): string => {
  return path === "/" ? "/onboarding" : `/onboarding/${path}`;
};

const getCurrentRouteIndex = (routes: OnboardingRoute[], currentPath: string): number => {
  const pathWithoutPrefix = currentPath.replace("/onboarding", "") || "/";
  const normalizedPath = pathWithoutPrefix === "/" ? "/" : pathWithoutPrefix.replace(/^\//, "");
  return routes.findIndex(route => route.path === normalizedPath);
};

const getNextOnboardingRoute = (routes: OnboardingRoute[], currentIndex: number): string | null => {
  if (currentIndex === -1 || currentIndex >= routes.length - 1) {
    return null;
  }

  const nextRoute = routes[currentIndex + 1];
  return getRouteUrlFromPath(nextRoute.path);
};

const getPreviousOnboardingRoute = (routes: OnboardingRoute[], currentIndex: number): string | null => {
  if (currentIndex <= 0) {
    return null;
  }

  const previousRoute = routes[currentIndex - 1];
  return getRouteUrlFromPath(previousRoute.path);
};

export const useOnboardingNavigation = () => {
  const { profile } = useProfileContext();
  const location = useLocation();

  invariant(profile);

  const routes = getOnboardingFlow(profile);
  const currentIndex = getCurrentRouteIndex(routes, location.pathname);
  const nextRoute = getNextOnboardingRoute(routes, currentIndex);
  const previousRoute = getPreviousOnboardingRoute(routes, currentIndex);

  return {
    nextRoute,
    previousRoute,
    currentIndex,
  };
};
